import { useCallback, useState } from "react";
import { mapLibraryRows, normalizeResourceOrder, normalizeResourceUrl, reorderList } from "../utils/resourceHelpers";

export default function useExternalResources({
  supabase,
  isAdmin,
  tableNames,
  userLibraryStorageKey,
  userDataKeyLibrary,
  loadUserPrivateData,
  saveUserPrivateData,
  showStatus,
  createId,
}) {
  const [externalResources, setExternalResources] = useState([]);
  const [libraryLoadInfo, setLibraryLoadInfo] = useState({
    source: "supabase",
    count: 0,
    error: "",
  });
  const [newResourceName, setNewResourceName] = useState("");
  const [newResourceUrl, setNewResourceUrl] = useState("");
  const [editingResource, setEditingResource] = useState(null);

  const persistUserLibrary = useCallback((nextResources) => {
    if (isAdmin) return;
    void saveUserPrivateData(userDataKeyLibrary, nextResources);
  }, [isAdmin, saveUserPrivateData, userDataKeyLibrary]);

  const loadExternalResources = useCallback(async (names = tableNames) => {
    if (!isAdmin) {
      const privateResources = await loadUserPrivateData(
        userDataKeyLibrary,
        userLibraryStorageKey,
        []
      );
      const resources = mapLibraryRows(Array.isArray(privateResources.value) ? privateResources.value : []);
      setExternalResources(resources);
      setLibraryLoadInfo({ source: "private", count: resources.length, error: "" });
      return;
    }

    if (!supabase) {
      setExternalResources([]);
      setLibraryLoadInfo({ source: "supabase", count: 0, error: "Supabase nicht konfiguriert" });
      return;
    }

    try {
      const { data, error } = await supabase
        .from(names.library)
        .select("id,name,url,order_index,created_at")
        .order("order_index", { ascending: true })
        .order("created_at", { ascending: true });
      if (error) throw error;

      if (!Array.isArray(data) || data.length === 0) {
        setExternalResources([]);
        setLibraryLoadInfo({ source: "supabase", count: 0, error: "" });
        return;
      }

      const mapped = mapLibraryRows(data);
      if (mapped.length) {
        setExternalResources(mapped);
        setLibraryLoadInfo({ source: "supabase", count: mapped.length, error: "" });
      } else {
        setExternalResources([]);
        setLibraryLoadInfo({ source: "supabase", count: 0, error: "" });
      }
    } catch (err) {
      console.error("Fehler beim Laden der Ressourcen:", err);
      setExternalResources([]);
      setLibraryLoadInfo({ source: "supabase", count: 0, error: err?.message || "Laden fehlgeschlagen" });
    }
  }, [isAdmin, loadUserPrivateData, supabase, tableNames, userDataKeyLibrary, userLibraryStorageKey]);

  const createExternalResource = useCallback(async () => {
    if (!newResourceName.trim()) { showStatus("Bitte Name eingeben.", true); return; }
    const normalizedUrl = normalizeResourceUrl(newResourceUrl);
    if (!normalizedUrl) { showStatus("Bitte eine gültige http(s)-URL eingeben.", true); return; }

    if (!isAdmin) {
      const nextResources = [
        ...externalResources,
        {
          id: `${createId()}-${Math.random()}`,
          name: newResourceName.trim(),
          url: normalizedUrl,
          order_index: externalResources.length,
        },
      ];
      setExternalResources(nextResources);
      persistUserLibrary(nextResources);
      setLibraryLoadInfo({ source: "private", count: nextResources.length, error: "" });
      setNewResourceName("");
      setNewResourceUrl("");
      showStatus("Ressource gespeichert ✓");
      return;
    }

    const nextOrder = externalResources.length;
    try {
      const { error } = await supabase
        .from(tableNames.library)
        .insert({ name: newResourceName.trim(), url: normalizedUrl, order_index: nextOrder, created_at: new Date().toISOString() });
      if (error) throw error;
      await loadExternalResources();
      setNewResourceName("");
      setNewResourceUrl("");
      showStatus("Ressource gespeichert ✓");
    } catch {
      showStatus("Fehler beim Speichern der Ressource.", true);
    }
  }, [createId, externalResources, isAdmin, loadExternalResources, newResourceName, newResourceUrl, persistUserLibrary, showStatus, supabase, tableNames]);

  const updateExternalResource = useCallback(async () => {
    if (!editingResource?.id) { showStatus("Ungültige Ressource.", true); return; }
    if (!editingResource.name?.trim()) { showStatus("Bitte Name eingeben.", true); return; }
    const normalizedUrl = normalizeResourceUrl(editingResource.url);
    if (!normalizedUrl) { showStatus("Bitte eine gültige http(s)-URL eingeben.", true); return; }

    if (!isAdmin) {
      const nextResources = externalResources.map((item) => item.id === editingResource.id
        ? { ...item, name: editingResource.name.trim(), url: normalizedUrl }
        : item);
      setExternalResources(nextResources);
      persistUserLibrary(nextResources);
      setEditingResource(null);
      showStatus("Ressource aktualisiert ✓");
      return;
    }

    try {
      const { error } = await supabase
        .from(tableNames.library)
        .update({ name: editingResource.name.trim(), url: normalizedUrl })
        .eq("id", editingResource.id);
      if (error) throw error;
      await loadExternalResources();
      setEditingResource(null);
      showStatus("Ressource aktualisiert ✓");
    } catch {
      showStatus("Fehler beim Aktualisieren der Ressource.", true);
    }
  }, [editingResource, externalResources, isAdmin, loadExternalResources, persistUserLibrary, showStatus, supabase, tableNames]);

  const persistExternalResourceOrder = useCallback(async (orderedResources) => {
    for (let i = 0; i < orderedResources.length; i += 1) {
      const item = orderedResources[i];
      if (!item?.id) continue;
      const { error } = await supabase.from(tableNames.library).update({ order_index: i }).eq("id", item.id);
      if (error) throw error;
    }
  }, [supabase, tableNames]);

  const deleteExternalResource = useCallback(async (resource) => {
    if (!resource?.id && (!resource?.name || !resource?.url)) {
      showStatus("Ungültige Ressource.", true);
      return;
    }

    if (!isAdmin) {
      const nextResources = normalizeResourceOrder(externalResources.filter((item) => item.id !== resource.id));
      setExternalResources(nextResources);
      persistUserLibrary(nextResources);
      setLibraryLoadInfo({ source: "private", count: nextResources.length, error: "" });
      showStatus("Ressource gelöscht ✓");
      return;
    }

    try {
      let deleteQuery = supabase.from(tableNames.library).delete();
      if (resource.id) {
        deleteQuery = deleteQuery.eq("id", resource.id);
      } else {
        // Fallback for legacy/fallback list entries without id
        deleteQuery = deleteQuery.eq("name", resource.name).eq("url", resource.url);
      }
      const { error } = await deleteQuery;
      if (error) throw error;

      const { data: remaining, error: remainingError } = await supabase
        .from(tableNames.library)
        .select("id,order_index,created_at")
        .order("order_index", { ascending: true })
        .order("created_at", { ascending: true });
      if (remainingError) throw remainingError;

      if (Array.isArray(remaining) && remaining.length) {
        await persistExternalResourceOrder(remaining);
      }

      await loadExternalResources();
      showStatus("Ressource gelöscht ✓");
    } catch {
      showStatus("Fehler beim Löschen der Ressource.", true);
    }
  }, [externalResources, isAdmin, loadExternalResources, persistExternalResourceOrder, persistUserLibrary, showStatus, supabase, tableNames]);

  const moveExternalResource = useCallback(async (resource, direction) => {
    if (!resource?.id) { showStatus("Ungültige Ressource.", true); return; }

    const currentIndex = externalResources.findIndex((item) => item.id === resource.id);
    if (currentIndex === -1) return;

    const targetIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= externalResources.length) return;

    const targetItem = externalResources[targetIndex];
    if (!targetItem?.id) {
      showStatus("Diese Ressource kann nicht verschoben werden.", true);
      return;
    }

    const reordered = reorderList(externalResources, currentIndex, targetIndex);

    if (!isAdmin) {
      const normalized = normalizeResourceOrder(reordered);
      setExternalResources(normalized);
      persistUserLibrary(normalized);
      setLibraryLoadInfo({ source: "private", count: normalized.length, error: "" });
      showStatus("Reihenfolge aktualisiert ✓");
      return;
    }

    try {
      await persistExternalResourceOrder(reordered);
      await loadExternalResources();
      showStatus("Reihenfolge aktualisiert ✓");
    } catch {
      showStatus("Fehler beim Aktualisieren der Reihenfolge.", true);
    }
  }, [externalResources, isAdmin, loadExternalResources, persistExternalResourceOrder, persistUserLibrary, showStatus]);

  const reorderExternalResource = useCallback(async (draggedResource, targetResource) => {
    if (!draggedResource?.id || !targetResource?.id) { showStatus("Ungültige Ressource.", true); return; }

    const fromIndex = externalResources.findIndex((item) => item.id === draggedResource.id);
    const toIndex = externalResources.findIndex((item) => item.id === targetResource.id);
    if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return;

    const reordered = reorderList(externalResources, fromIndex, toIndex);

    if (!isAdmin) {
      const normalized = normalizeResourceOrder(reordered);
      setExternalResources(normalized);
      persistUserLibrary(normalized);
      setLibraryLoadInfo({ source: "private", count: normalized.length, error: "" });
      showStatus("Reihenfolge aktualisiert ✓");
      return;
    }

    try {
      await persistExternalResourceOrder(reordered);
      await loadExternalResources();
      showStatus("Reihenfolge aktualisiert ✓");
    } catch {
      showStatus("Fehler beim Aktualisieren der Reihenfolge.", true);
    }
  }, [externalResources, isAdmin, loadExternalResources, persistExternalResourceOrder, persistUserLibrary, showStatus]);

  const resetExternalResourceDrafts = useCallback(() => {
    setNewResourceName("");
    setNewResourceUrl("");
    setEditingResource(null);
  }, []);

  return {
    externalResources,
    libraryLoadInfo,
    newResourceName,
    setNewResourceName,
    newResourceUrl,
    setNewResourceUrl,
    editingResource,
    setEditingResource,
    loadExternalResources,
    createExternalResource,
    updateExternalResource,
    deleteExternalResource,
    moveExternalResource,
    reorderExternalResource,
    resetExternalResourceDrafts,
  };
}
